import { useEffect, useState } from 'react';

import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import VideoResource from 'types/VideoResource';

import { isExclusiveLocked } from '../VideoResources/VideoResources.helpers';

interface UseWebinarInteractionArgs {
  filteredResources: VideoResource[] | undefined;
  isAnyTinyPlus: boolean;
}

// Owns the webinars section's interaction: which video is featured, the upsell
// modal, and what happens on a card click. Kept out of the presentational
// component so selection/analytics/scroll behavior changes in one place.
export const useWebinarInteraction = ({
  filteredResources,
  isAnyTinyPlus,
}: UseWebinarInteractionArgs) => {
  const { trackEvent } = useAnalytics();

  const [selectedVideo, setSelectedVideo] = useState<VideoResource | null>(
    null,
  );
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upsellTriggerVideoId, setUpsellTriggerVideoId] = useState<
    number | undefined
  >(undefined);

  // Re-pick the featured video when the selection drops out of the filtered
  // list. Prefer a featured viewable item, then any viewable, then the first.
  useEffect(() => {
    if (!filteredResources) return; // still loading
    // Nothing matched — clear the stale selection so the empty state shows.
    if (!filteredResources.length) {
      if (selectedVideo) setSelectedVideo(null);
      return;
    }
    const selectionStillVisible =
      selectedVideo && filteredResources.some(v => v.id === selectedVideo.id);
    if (selectionStillVisible) return;
    const viewable = (video: VideoResource) =>
      !video.is_tiny_plus_only || isAnyTinyPlus;
    const featured = filteredResources.find(v => v.is_featured && viewable(v));
    const fallback = filteredResources.find(viewable);
    setSelectedVideo(featured ?? fallback ?? filteredResources[0]);
  }, [filteredResources, selectedVideo, isAnyTinyPlus]);

  const handleCardSelect = (video: VideoResource) => {
    if (video.is_tiny_plus_only) {
      trackEvent('Tiny Plus Exclusive Resource Clicked', {
        is_member: isAnyTinyPlus,
        resource_type: 'webinar',
        resource_id: video.id,
        title: video.short_title,
      });
    }

    // Non-member clicking exclusive content: show the locked thumbnail and open
    // the upsell. The player hook tears down the iframe when the lock flips.
    if (isExclusiveLocked(video, isAnyTinyPlus)) {
      setSelectedVideo(video);
      setUpsellTriggerVideoId(video.id);
      setShowUpgradeModal(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!video.video_url) return;
    trackEvent('Resource Content Clicked', {
      resource_type: 'video',
      content_title: video.full_title ?? video.short_title ?? '',
    });
    trackEvent('Video Played', {
      video_title: video.full_title ?? video.short_title ?? '',
      source: 'resources_page',
    });
    setSelectedVideo(video);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return {
    selectedVideo,
    showUpgradeModal,
    upsellTriggerVideoId,
    closeUpgradeModal: () => setShowUpgradeModal(false),
    handleCardSelect,
  };
};
